/**
 * Standard Card Component
 *
 * Displays an official standard with status badge and contextual actions.
 * Actions are contextual based on the standard's current status.
 */

// TODO: Add proper admin role check before allowing mutations

import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  CheckCircle,
  XCircle,
  Trash2,
  ArrowDownCircle,
  FileText,
  Power,
  Pencil,
  RefreshCw,
} from "lucide-react";
import { api } from "convex/_generated/api";
import { Button } from "@/components/ui/primitives";
import { ConfirmDeleteDialog } from "@/components/ui/ConfirmDeleteDialog";
import { EditStandardDialog } from "./EditStandardDialog";
import { useApiMutation } from "@/packages/features-shared";
import { useTranslation, getCurrentLanguage } from "@/packages/utils";
import { useWorkspaceId } from "@/hooks/use-workspace-mutation";
import type { FunctionReturnType } from "convex/server";

type OfficialStandard = FunctionReturnType<
  typeof api.admin_standards_queries.list
>[number];

type StandardStatus = OfficialStandard["status"];

/* ------------------------------------------------------------------ */
/* Status badge configuration                                          */
/* ------------------------------------------------------------------ */

function StatusBadge({ status }: { status: StandardStatus }) {
  const { t } = useTranslation("admin");
  const STATUS_STYLES: Record<
    StandardStatus,
    { bgColor: string; textColor: string; pulse?: boolean }
  > = {
    uploading: {
      bgColor: "rgba(161,161,166,0.15)",
      textColor: "#a1a1a6",
    },
    extracting: {
      bgColor: "rgba(234,179,8,0.15)",
      textColor: "#ca8a04",
      pulse: true,
    },
    extracted: {
      bgColor: "rgba(59,130,246,0.15)",
      textColor: "#3b82f6",
    },
    extraction_failed: {
      bgColor: "rgba(239,68,68,0.15)",
      textColor: "#ef4444",
    },
    approved: {
      bgColor: "rgba(99,102,241,0.15)",
      textColor: "#6366f1",
    },
    generating_checklist: {
      bgColor: "rgba(234,179,8,0.15)",
      textColor: "#ca8a04",
      pulse: true,
    },
    checklist_generation_failed: {
      bgColor: "rgba(239,68,68,0.15)",
      textColor: "#ef4444",
    },
    published: {
      bgColor: "rgba(34,197,94,0.15)",
      textColor: "#22c55e",
    },
    rejected: {
      bgColor: "rgba(239,68,68,0.15)",
      textColor: "#ef4444",
    },
    translation_incomplete: {
      bgColor: "rgba(234,179,8,0.15)",
      textColor: "#d97706",
    },
  };

  const config = STATUS_STYLES[status];
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium ${config.pulse ? "animate-pulse" : ""}`}
      style={{ backgroundColor: config.bgColor, color: config.textColor }}
    >
      {t(`standards.status.${status}`)}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Date formatter                                                      */
/* ------------------------------------------------------------------ */

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/* ------------------------------------------------------------------ */
/* Standard Card                                                       */
/* ------------------------------------------------------------------ */

interface StandardCardProps {
  standard: OfficialStandard;
}

export function StandardCard({ standard }: StandardCardProps) {
  const { t } = useTranslation(["admin", "common"]);
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const isActive = standard.isActive !== false;

  const approveMutation = useApiMutation(
    api.admin_standards_mutations.approveExtraction,
    {
      name: "approve-extraction",
      successMessage: t("admin:standards.actions.generatingChecklist"),
    },
  );
  const rejectMutation = useApiMutation(
    api.admin_standards_mutations.rejectExtraction,
    {
      name: "reject-extraction",
      successMessage: t("admin:standards.actions.rejectLabel"),
    },
  );
  const unpublishMutation = useApiMutation(
    api.admin_standards_mutations.unpublish,
    {
      name: "unpublish-standard",
      successMessage: t("admin:standards.actions.unpublishLabel"),
    },
  );
  const deleteMutation = useApiMutation(
    api.admin_standards_mutations.deleteStandard,
    {
      name: "delete-standard",
      successMessage: t("admin:standards.actions.delete"),
    },
  );
  const toggleActiveMutation = useApiMutation(
    api.admin_standards_mutations.toggleActive,
    {
      name: "toggle-active",
      successMessage: isActive
        ? t("admin:standards.active.inactive")
        : t("admin:standards.active.label"),
    },
  );
  const checkStatusMutation = useApiMutation(
    api.admin_standards_mutations.checkExtractionStatus,
    {
      name: "check-extraction-status",
    },
  );

  const handleView = () => {
    navigate({
      to: "/$workspaceSlug/admin/standards/$standardId",
      params: { workspaceSlug: workspaceSlug ?? "", standardId: standard._id },
    });
  };

  const handleApprove = async () => {
    await approveMutation.mutateAsync({
      standardId: standard._id,
      workspaceId,
      locale: getCurrentLanguage(),
    });
  };

  const handleReject = async () => {
    await rejectMutation.mutateAsync({ standardId: standard._id });
  };

  const handleUnpublish = async () => {
    await unpublishMutation.mutateAsync({ standardId: standard._id });
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    await deleteMutation.mutateAsync({ standardId: standard._id });
    setDeleteDialogOpen(false);
  };

  const handleToggleActive = async () => {
    await toggleActiveMutation.mutateAsync({
      standardId: standard._id,
      isActive: !isActive,
    });
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleCheckStatus = async () => {
    await checkStatusMutation.mutateAsync({ standardId: standard._id });
  };

  /* ---- Contextual action buttons ---- */
  const actions = getContextualActions(
    standard.status,
    {
      edit: t("admin:standards.actions.editLabel"),
      delete: t("admin:standards.actions.delete"),
      approve: t("admin:standards.actions.approveLabel"),
      reject: t("admin:standards.actions.rejectLabel"),
      unpublish: t("admin:standards.actions.unpublishLabel"),
      checkStatus: t("admin:standards.actions.checkStatus"),
    },
    {
      onApprove: handleApprove,
      onReject: handleReject,
      onUnpublish: handleUnpublish,
      onDelete: handleDelete,
      onEdit: handleEdit,
      onCheckStatus: handleCheckStatus,
    },
  );

  /* ---- Counts display ---- */
  const hasCounts =
    standard.extractedClausesCount > 0 || standard.checklistItemsCount > 0;

  return (
    <>
      <button
        type="button"
        onClick={handleView}
        className={`w-full text-left rounded-xl border border-border bg-card p-4 transition-colors hover:bg-card-hover/30 cursor-pointer ${!isActive ? "opacity-60" : ""}`}
      >
        {/* Row 1: Template code + Status badge + Active toggle */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-accent/70 shrink-0" />
            <span className="text-[14px] font-semibold text-primary">
              {standard.templateCode}
            </span>
            {standard.templateDisplayName && (
              <span className="text-[12px] text-muted">
                {standard.templateDisplayName}
              </span>
            )}
            {!isActive && (
              <span className="inline-block rounded-full bg-muted/20 px-2 py-0.5 text-[10px] font-medium text-muted">
                {t("admin:standards.active.inactive")}
              </span>
            )}
            {isActive && (
              <span
                className="inline-block rounded-full px-2 py-0.5 text-[10px] font-medium"
                style={{
                  backgroundColor: "rgba(34,197,94,0.15)",
                  color: "#22c55e",
                }}
              >
                {t("admin:standards.active.label")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isActive && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleActive();
                }}
                disabled={toggleActiveMutation.isPending}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors cursor-pointer bg-green-500/10 text-green-600 hover:bg-green-500/20"
                title={t("admin:standards.setActive")}
              >
                <Power size={12} />
                {t("admin:standards.setActive")}
              </button>
            )}
            <StatusBadge status={standard.status} />
          </div>
        </div>

        {/* Row 2: File name */}
        <p className="text-[13px] text-secondary truncate mb-2">
          {standard.fileName}
        </p>

        {/* Row 3: Counts + Date + Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {hasCounts && (
              <span className="text-[12px] text-muted">
                {standard.extractedClausesCount > 0 &&
                  t("admin:standards.counts.clauses", {
                    count: standard.extractedClausesCount,
                  })}
                {standard.extractedClausesCount > 0 &&
                  standard.checklistItemsCount > 0 &&
                  " / "}
                {standard.checklistItemsCount > 0 &&
                  t("admin:standards.counts.checklistItems", {
                    count: standard.checklistItemsCount,
                  })}
              </span>
            )}
            <span className="text-[12px] text-muted">
              {formatDate(standard._creationTime)}
            </span>
          </div>

          {/* biome-ignore lint/a11y/useKeyWithClickEvents: stop propagation wrapper */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: stop propagation wrapper */}
          <div
            className="flex items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant}
                size="sm"
                icon={action.icon}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </div>
      </button>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        loading={deleteMutation.isPending}
        title={t("admin:standards.deleteConfirm.title")}
        description={t("admin:standards.deleteConfirm.description")}
        confirmMatchText={standard.templateCode ?? standard.fileName}
        confirmLabel={t("admin:standards.deleteConfirm.confirmLabel")}
      />

      <EditStandardDialog
        standard={standard}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Contextual action helpers                                           */
/* ------------------------------------------------------------------ */

interface ActionHandlers {
  onApprove: () => void;
  onReject: () => void;
  onUnpublish: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onCheckStatus: () => void;
}

interface ActionConfig {
  label: string;
  icon: typeof Trash2;
  variant: "secondary" | "primary" | "ghost" | "danger";
  onClick: () => void;
}

interface ActionLabels {
  edit: string;
  delete: string;
  approve: string;
  reject: string;
  unpublish: string;
  checkStatus: string;
}

function getContextualActions(
  status: StandardStatus,
  labels: ActionLabels,
  handlers: ActionHandlers,
): ActionConfig[] {
  const editAction: ActionConfig = {
    label: labels.edit,
    icon: Pencil,
    variant: "ghost",
    onClick: handlers.onEdit,
  };

  const deleteAction: ActionConfig = {
    label: labels.delete,
    icon: Trash2,
    variant: "danger",
    onClick: handlers.onDelete,
  };

  switch (status) {
    case "extracted":
      return [
        editAction,
        {
          label: labels.approve,
          icon: CheckCircle,
          variant: "primary",
          onClick: handlers.onApprove,
        },
        {
          label: labels.reject,
          icon: XCircle,
          variant: "danger",
          onClick: handlers.onReject,
        },
        deleteAction,
      ];
    case "translation_incomplete":
      return [
        editAction,
        {
          label: labels.approve,
          icon: CheckCircle,
          variant: "primary",
          onClick: handlers.onApprove,
        },
        deleteAction,
      ];
    case "published":
      return [
        editAction,
        {
          label: labels.unpublish,
          icon: ArrowDownCircle,
          variant: "secondary",
          onClick: handlers.onUnpublish,
        },
        deleteAction,
      ];
    case "extracting":
    case "generating_checklist":
      return [
        {
          label: labels.checkStatus,
          icon: RefreshCw,
          variant: "secondary",
          onClick: handlers.onCheckStatus,
        },
      ];
    default:
      return [editAction, deleteAction];
  }
}
